import { accountObj, agencyObj, userObj } from '../store'
import * as request from '../utils/request'
import { isObject, hasOwn, getValueByPath } from '../utils/helpers'
import { $accountDefaults } from '@akerolabs/akero-constants/src/constants/accountsConfig'

/**
 * @type {Object}
 * @description The default response for a failed feature check
 */
const disallowed = {
  access: false,
  setting: false,
  agency: false,
}

/**
 * @type {Object}
 * @description A simple object to cache feature checks
 */
const cache = {
  features: {},
  state: {
    account: '',
    agency: '',
  },
}

/**
 * @type {AsyncFunction}
 * @returns {Promise}
 * @description Returns a promise to fetch the current account data fresh
 * from the API.
 */
async function getAccount() {
  return request.get(`/accounts/${accountObj()._id}`, {
    params: {
      agency: agencyObj()._id,
      populate: 'agency',
    },
  })
}

/**
 * @param {String} feature - The feature to look for
 * @returns {Object|Undefined}
 * @description Tries to get a cached config for the requested feature from
 * the account
 */
function getCachedResult(feature) {
  const { _id: account } = accountObj()
  const { _id: agency } = agencyObj()

  if (account !== cache.state.account || agency !== cache.state.agency) {
    cache.state.account = account
    cache.state.agency = agency
    cache.features = {}
  }

  return cache.features[feature]
}

/**
 * @param {object} agency - the agnecy to get the settings for
 * @param {Object} account - the account to get the settings for
 * @param {Object} user - the user data for the current session
 * @param {String} feature - the feature to check
 * @param {Boolean} setCache - flag to use the cache
 * @returns {Object}
 * @description Tries to find the setting for the requested feature, this will check at
 * both the account and agency level to see if the feature is allowed.
 */
export function getAllowed(agency, account, user, feature, setCache) {
  if (
    !isObject(account) ||
    !isObject(agency) ||
    !hasOwn($accountDefaults, feature)
  ) {
    return disallowed
  }

  const agencySetting = agency.accountDefaults.features[feature]
  const accountSetting = getValueByPath(
    account,
    $accountDefaults[feature].accountPath,
  )

  if (!agencySetting || !accountSetting) {
    return disallowed
  }

  const setting =
    accountSetting !== 'default' ? accountSetting : agencySetting.setting

  const hasAccess =
    setting === 'agency'
      ? ['agencyuser', 'agencyadmin', 'superadmin', 'developer'].indexOf(
          user.type,
        ) !== -1
      : setting === 'true'

  const enabledOnAgency = agencySetting.enabled
  const access = hasAccess && enabledOnAgency
  const output = { setting, access, agency: enabledOnAgency }

  if (setCache === true) {
    cache.features[feature] = { ...output, cacheHit: true }
  }

  return output
}

export async function isAllowedAsync(feature) {
  const cached = getCachedResult(feature)
  if (isObject(cached)) return cached
  const { data: res } = await getAccount()
  const { data: account } = res
  const { agency } = account
  return getAllowed(agency, account, userObj(), feature, true)
}

/**
 * @param {String} feature - The feature to check
 * @returns {Object}
 * @description Checks the current account and agency to check that the
 * current account has the requested feature enabled, this will use data
 * that has been set via the drilldowns, but not make a request to the API.
 */
export function isAllowedSync(feature) {
  return getAllowed(agencyObj(), accountObj(), userObj(), feature, false)
}

/**
 * @param {String} feature - The feature name to check
 * @description Checks to see if an account has a certain feature enabled
 */
export function isAllowed(feature, promise = true) {
  if (promise === true) {
    return isAllowedAsync(feature)
  } else {
    return isAllowedSync(feature)
  }
}

/**
 * @returns {Array} - the list of enabled features
 * @description Returns a list of active features for the current account
 */
export function featureList() {
  return Object.keys($accountDefaults).reduce((acc, i) => {
    if (isAllowedSync(i).access) {
      acc.push($accountDefaults[i].label)
    }
    return acc
  }, [])
}

/**
 * @description Resets the cahce.
 */
export function clearCache() {
  cache.state.account = null
  cache.state.agency = null
  cache.features = null
}
